import React, { useState, useEffect } from 'react';
import { getEmergencyInformation } from '../helpers/emergency';
import api from '../helpers/api';

const EmergencyInformation = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [emergencyData, setEmergencyData] = useState({
        components_emergency_emergency_contacts: [],
        components_emergency_medical_facilities: [],
        components_emergency_safety_infos: []
    });

    // For adding new items
    const [newContact, setNewContact] = useState({ name: '', phoneNumber: '', description: '' });
    const [newFacility, setNewFacility] = useState({
        name: '',
        address: '',
        phone: '',
        type: 'hospital',
        latitude: '',
        longitude: ''
    });
    const [newSafetyInfo, setNewSafetyInfo] = useState({ title: '', content: '', priority: 0 });

    useEffect(() => {
        fetchEmergencyData();
    }, []);

    const fetchEmergencyData = async () => {
        try {
            setIsLoading(true);
            const data = await getEmergencyInformation();
            // console.log(data)
            setEmergencyData({
                components_emergency_emergency_contacts: data.components_emergency_emergency_contacts || [],
                components_emergency_medical_facilities: data.components_emergency_medical_facilities || [],
                components_emergency_safety_infos: data.components_emergency_safety_infos || []
            });
        } catch (err) {
            setError('Erreur lors du chargement des informations d\'urgence');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleContactChange = (index, field, value) => {
        const updatedContacts = [...emergencyData.components_emergency_emergency_contacts];
        updatedContacts[index] = {
            ...updatedContacts[index],
            [field]: value
        };

        setEmergencyData({
            ...emergencyData,
            components_emergency_emergency_contacts: updatedContacts
        });
    };

    const handleFacilityChange = (index, field, value) => {
        const updatedFacilities = [...emergencyData.components_emergency_medical_facilities];
        updatedFacilities[index] = {
            ...updatedFacilities[index],
            [field]: value
        };

        setEmergencyData({
            ...emergencyData,
            components_emergency_medical_facilities: updatedFacilities
        });
    };

    const handleSafetyInfoChange = (index, field, value) => {
        const updatedSafetyInfos = [...emergencyData.components_emergency_safety_infos];
        updatedSafetyInfos[index] = {
            ...updatedSafetyInfos[index],
            [field]: value
        };

        setEmergencyData({
            ...emergencyData,
            components_emergency_safety_infos: updatedSafetyInfos
        });
    };

    const handleNewContactChange = (field, value) => {
        setNewContact({
            ...newContact,
            [field]: value
        });
    };

    const handleNewFacilityChange = (field, value) => {
        setNewFacility({
            ...newFacility,
            [field]: value
        });
    };

    const handleNewSafetyInfoChange = (field, value) => {
        setNewSafetyInfo({
            ...newSafetyInfo,
            [field]: value
        });
    };

    const handleUpdateContact = async (index) => {
        try {
            // Extraire uniquement les champs nécessaires du contact
            const contactToUpdate = emergencyData.components_emergency_emergency_contacts[index];

            // Créer un nouvel objet contact sans les ID
            const cleanedContact = {
                name: contactToUpdate.name,
                phoneNumber: contactToUpdate.phoneNumber,
                description: contactToUpdate.description
            };

            // Récupérer tous les contacts actuels
            const allContacts = emergencyData.components_emergency_emergency_contacts.map((contact, i) => {
                // Pour le contact mis à jour, utiliser les données nettoyées
                if (i === index) {
                    return cleanedContact;
                }
                // Pour les autres contacts, créer de nouveaux objets sans les ID
                return {
                    name: contact.name,
                    phoneNumber: contact.phoneNumber,
                    description: contact.description
                };
            });

            // Envoyer directement à l'API sans utiliser les fonctions helper
            await api.put(`/emergency-informations/v8f0ijxniss7pucxy3alvasz`, {
                data: {
                    components_emergency_emergency_contacts: allContacts
                }
            });

            showSuccessMessage('Contact d\'urgence mis à jour avec succès');
        } catch (err) {
            setError('Erreur lors de la mise à jour du contact d\'urgence');
            console.error(err);
        }
    };

    const handleUpdateFacility = async (index) => {
        try {
            // Extraire uniquement les champs nécessaires de l'établissement médical
            const facilityToUpdate = emergencyData.components_emergency_medical_facilities[index];

            // Créer un nouvel objet établissement sans les ID
            const cleanedFacility = {
                name: facilityToUpdate.name,
                address: facilityToUpdate.address,
                phone: facilityToUpdate.phone,
                type: facilityToUpdate.type,
                latitude: facilityToUpdate.latitude,
                longitude: facilityToUpdate.longitude
            };

            // Récupérer tous les établissements actuels
            const allFacilities = emergencyData.components_emergency_medical_facilities.map((facility, i) => {
                // Pour l'établissement mis à jour, utiliser les données nettoyées
                if (i === index) {
                    return cleanedFacility;
                }
                // Pour les autres établissements, créer de nouveaux objets sans les ID
                return {
                    name: facility.name,
                    address: facility.address,
                    phone: facility.phone,
                    type: facility.type,
                    latitude: facility.latitude,
                    longitude: facility.longitude
                };
            });

            // Envoyer directement à l'API sans utiliser les fonctions helper
            await api.put(`/emergency-informations/v8f0ijxniss7pucxy3alvasz`, {
                data: {
                    components_emergency_medical_facilities: allFacilities
                }
            });

            showSuccessMessage('Établissement médical mis à jour avec succès');
        } catch (err) {
            setError('Erreur lors de la mise à jour de l\'établissement médical');
            console.error(err);
        }
    };

    const handleUpdateSafetyInfo = async (index) => {
        try {
            // Extraire uniquement les champs nécessaires de l'info de sécurité
            const safetyToUpdate = emergencyData.components_emergency_safety_infos[index];

            // Obtenir le contenu de l'éditeur si disponible
            let content = safetyToUpdate.content;

            // Créer un nouvel objet info de sécurité sans les ID
            const cleanedSafety = {
                title: safetyToUpdate.title,
                content: content,
                priority: safetyToUpdate.priority
            };

            // Récupérer toutes les infos de sécurité actuelles
            const allSafetyInfos = emergencyData.components_emergency_safety_infos.map((safety, i) => {
                // Pour l'info mise à jour, utiliser les données nettoyées
                if (i === index) {
                    return cleanedSafety;
                }
                // Pour les autres infos, créer de nouveaux objets sans les ID
                return {
                    title: safety.title,
                    content: safety.content,
                    priority: safety.priority
                };
            });

            // Envoyer directement à l'API sans utiliser les fonctions helper
            await api.put(`/emergency-informations/v8f0ijxniss7pucxy3alvasz`, {
                data: {
                    components_emergency_safety_infos: allSafetyInfos
                }
            });

            showSuccessMessage('Information de sécurité mise à jour avec succès');
        } catch (err) {
            setError('Erreur lors de la mise à jour de l\'information de sécurité');
            console.error(err);
        }
    };

    // De même pour les fonctions d'ajout

    const handleAddContact = async () => {
        try {
            // Créer un nouvel objet contact avec uniquement les champs nécessaires
            const cleanedContact = {
                name: newContact.name,
                phoneNumber: newContact.phoneNumber,
                description: newContact.description
            };

            // Récupérer tous les contacts actuels et ajouter le nouveau
            const allContacts = emergencyData.components_emergency_emergency_contacts.map(contact => ({
                name: contact.name,
                phoneNumber: contact.phoneNumber,
                description: contact.description
            }));

            allContacts.push(cleanedContact);

            // Envoyer directement à l'API sans utiliser les fonctions helper
            await api.put(`/emergency-informations/v8f0ijxniss7pucxy3alvasz`, {
                data: {
                    components_emergency_emergency_contacts: allContacts
                }
            });

            // Mettre à jour l'état local
            setEmergencyData({
                ...emergencyData,
                components_emergency_emergency_contacts: [
                    ...emergencyData.components_emergency_emergency_contacts,
                    cleanedContact
                ]
            });

            setNewContact({ name: '', phoneNumber: '', description: '' });
            showSuccessMessage('Contact d\'urgence ajouté avec succès');
        } catch (err) {
            setError('Erreur lors de l\'ajout du contact d\'urgence');
            console.error(err);
        }
    };

    const handleAddFacility = async () => {
        try {
            // Créer un nouvel objet établissement avec uniquement les champs nécessaires
            const cleanedFacility = {
                name: newFacility.name,
                address: newFacility.address,
                phone: newFacility.phone,
                type: newFacility.type,
                latitude: newFacility.latitude,
                longitude: newFacility.longitude
            };

            // Récupérer tous les établissements actuels et ajouter le nouveau
            const allFacilities = emergencyData.components_emergency_medical_facilities.map(facility => ({
                name: facility.name,
                address: facility.address,
                phone: facility.phone,
                type: facility.type,
                latitude: facility.latitude,
                longitude: facility.longitude
            }));

            allFacilities.push(cleanedFacility);

            // Envoyer directement à l'API sans utiliser les fonctions helper
            await api.put(`/emergency-informations/v8f0ijxniss7pucxy3alvasz`, {
                data: {
                    components_emergency_medical_facilities: allFacilities
                }
            });

            // Mettre à jour l'état local
            setEmergencyData({
                ...emergencyData,
                components_emergency_medical_facilities: [
                    ...emergencyData.components_emergency_medical_facilities,
                    cleanedFacility
                ]
            });

            setNewFacility({
                name: '',
                address: '',
                phone: '',
                type: 'hospital',
                latitude: '',
                longitude: ''
            });

            showSuccessMessage('Établissement médical ajouté avec succès');
        } catch (err) {
            setError('Erreur lors de l\'ajout de l\'établissement médical');
            console.error(err);
        }
    };

    const handleAddSafetyInfo = async () => {
        try {
            // Créer un nouvel objet info de sécurité avec uniquement les champs nécessaires
            const cleanedSafety = {
                title: newSafetyInfo.title,
                content: newSafetyInfo.content,
                priority: newSafetyInfo.priority
            };

            // Récupérer toutes les infos de sécurité actuelles et ajouter la nouvelle
            const allSafetyInfos = emergencyData.components_emergency_safety_infos.map(safety => ({
                title: safety.title,
                content: safety.content,
                priority: safety.priority
            }));

            allSafetyInfos.push(cleanedSafety);

            // Envoyer directement à l'API sans utiliser les fonctions helper
            await api.put(`/emergency-informations/v8f0ijxniss7pucxy3alvasz`, {
                data: {
                    components_emergency_safety_infos: allSafetyInfos
                }
            });

            // Mettre à jour l'état local
            setEmergencyData({
                ...emergencyData,
                components_emergency_safety_infos: [
                    ...emergencyData.components_emergency_safety_infos,
                    cleanedSafety
                ]
            });

            setNewSafetyInfo({ title: '', content: '', priority: 0 });
            showSuccessMessage('Information de sécurité ajoutée avec succès');
        } catch (err) {
            setError('Erreur lors de l\'ajout de l\'information de sécurité');
            console.error(err);
        }
    };

    const handleRemoveContact = async (index) => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce contact d\'urgence ?')) {
            return;
        }

        try {
            // Récupérer tous les contacts actuels sauf celui à supprimer
            const updatedContacts = emergencyData.components_emergency_emergency_contacts
                .filter((_, i) => i !== index)
                .map(contact => ({
                    // Créer de nouveaux objets sans ID pour chaque contact
                    name: contact.name,
                    phoneNumber: contact.phoneNumber,
                    description: contact.description
                }));

            // Envoyer directement à l'API sans utiliser les fonctions helper
            await api.put(`/emergency-informations/v8f0ijxniss7pucxy3alvasz`, {
                data: {
                    components_emergency_emergency_contacts: updatedContacts
                }
            });

            // Mettre à jour l'état local
            const newContacts = [...emergencyData.components_emergency_emergency_contacts];
            newContacts.splice(index, 1);

            setEmergencyData({
                ...emergencyData,
                components_emergency_emergency_contacts: newContacts
            });

            showSuccessMessage('Contact d\'urgence supprimé avec succès');
        } catch (err) {
            setError('Erreur lors de la suppression du contact d\'urgence');
            console.error(err);
        }
    };

    const handleRemoveFacility = async (index) => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet établissement médical ?')) {
            return;
        }

        try {
            // Récupérer tous les établissements actuels sauf celui à supprimer
            const updatedFacilities = emergencyData.components_emergency_medical_facilities
                .filter((_, i) => i !== index)
                .map(facility => ({
                    // Créer de nouveaux objets sans ID pour chaque établissement
                    name: facility.name,
                    address: facility.address,
                    phone: facility.phone,
                    type: facility.type,
                    latitude: facility.latitude,
                    longitude: facility.longitude
                }));

            // Envoyer directement à l'API sans utiliser les fonctions helper
            await api.put(`/emergency-informations/v8f0ijxniss7pucxy3alvasz`, {
                data: {
                    components_emergency_medical_facilities: updatedFacilities
                }
            });

            // Mettre à jour l'état local
            const newFacilities = [...emergencyData.components_emergency_medical_facilities];
            newFacilities.splice(index, 1);

            setEmergencyData({
                ...emergencyData,
                components_emergency_medical_facilities: newFacilities
            });

            showSuccessMessage('Établissement médical supprimé avec succès');
        } catch (err) {
            setError('Erreur lors de la suppression de l\'établissement médical');
            console.error(err);
        }
    };

    const handleRemoveSafetyInfo = async (index) => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette information de sécurité ?')) {
            return;
        }

        try {
            // Récupérer toutes les infos de sécurité actuelles sauf celle à supprimer
            const updatedSafetyInfos = emergencyData.components_emergency_safety_infos
                .filter((_, i) => i !== index)
                .map(safety => ({
                    // Créer de nouveaux objets sans ID pour chaque info de sécurité
                    title: safety.title,
                    content: safety.content,
                    priority: safety.priority
                }));

            // Envoyer directement à l'API sans utiliser les fonctions helper
            await api.put(`/emergency-informations/v8f0ijxniss7pucxy3alvasz`, {
                data: {
                    components_emergency_safety_infos: updatedSafetyInfos
                }
            });

            // Mettre à jour l'état local
            const newSafetyInfos = [...emergencyData.components_emergency_safety_infos];
            newSafetyInfos.splice(index, 1);

            setEmergencyData({
                ...emergencyData,
                components_emergency_safety_infos: newSafetyInfos
            });

            showSuccessMessage('Information de sécurité supprimée avec succès');
        } catch (err) {
            setError('Erreur lors de la suppression de l\'information de sécurité');
            console.error(err);
        }
    };

    const showSuccessMessage = (message) => {
        setSuccessMessage(message);
        setTimeout(() => {
            setSuccessMessage('');
        }, 3000);
    };

    if (isLoading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Chargement...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid px-4">
            <h2 className="mb-4">Informations d'urgence</h2>

            {successMessage && (
                <div className="alert alert-success alert-dismissible fade show" role="alert">
                    {successMessage}
                    <button
                        type="button"
                        className="btn-close"
                        onClick={() => setSuccessMessage('')}>
                    </button>
                </div>
            )}

            {error && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                    {error}
                    <button
                        type="button"
                        className="btn-close"
                        onClick={() => setError('')}>
                    </button>
                </div>
            )}

            <div className="row">
                {/* Contacts d'urgence */}
                <div className="col-md-12 mb-4">
                    <div className="card shadow-sm">
                        <div className="card-header bg-primary text-white">
                            <h5 className="mb-0">Contacts d'urgence</h5>
                        </div>
                        <div className="card-body">
                            <div className="table-responsive mb-3">
                                <table className="table table-bordered table-hover">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Nom</th>
                                            <th>Numéro de téléphone</th>
                                            <th>Description</th>
                                            <th style={{ width: '150px' }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {emergencyData.components_emergency_emergency_contacts.map((contact, index) => (
                                            <tr key={index}>
                                                <td>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={contact.name || ''}
                                                        onChange={(e) => handleContactChange(index, 'name', e.target.value)}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={contact.phoneNumber || ''}
                                                        onChange={(e) => handleContactChange(index, 'phoneNumber', e.target.value)}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={contact.description || ''}
                                                        onChange={(e) => handleContactChange(index, 'description', e.target.value)}
                                                    />
                                                </td>
                                                <td>
                                                    <div className="btn-group">
                                                        <button
                                                            className="btn btn-sm btn-success"
                                                            onClick={() => handleUpdateContact(index)}
                                                            title="Enregistrer">
                                                            <i className="bi bi-check-lg"></i>
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-danger"
                                                            onClick={() => handleRemoveContact(index)}
                                                            title="Supprimer">
                                                            <i className="bi bi-trash"></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="card bg-light">
                                <div className="card-header">
                                    <h6 className="mb-0">Ajouter un nouveau contact</h6>
                                </div>
                                <div className="card-body">
                                    <div className="row g-3">
                                        <div className="col-md-4">
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Nom"
                                                value={newContact.name}
                                                onChange={(e) => handleNewContactChange('name', e.target.value)}
                                            />
                                        </div>
                                        <div className="col-md-3">
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Numéro de téléphone"
                                                value={newContact.phoneNumber}
                                                onChange={(e) => handleNewContactChange('phoneNumber', e.target.value)}
                                            />
                                        </div>
                                        <div className="col-md-4">
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Description"
                                                value={newContact.description}
                                                onChange={(e) => handleNewContactChange('description', e.target.value)}
                                            />
                                        </div>
                                        <div className="col-md-1">
                                            <button
                                                className="btn btn-primary w-100"
                                                onClick={handleAddContact}
                                                disabled={!newContact.name || !newContact.phoneNumber}>
                                                <i className="bi bi-plus-lg"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Établissements médicaux */}
                <div className="col-md-12 mb-4">
                    <div className="card shadow-sm">
                        <div className="card-header bg-success text-white">
                            <h5 className="mb-0">Établissements médicaux</h5>
                        </div>
                        <div className="card-body">
                            <div className="table-responsive mb-3">
                                <table className="table table-bordered table-hover">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Nom</th>
                                            <th>Adresse</th>
                                            <th>Téléphone</th>
                                            <th>Type</th>
                                            <th>Latitude</th>
                                            <th>Longitude</th>
                                            <th style={{ width: '150px' }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {emergencyData.components_emergency_medical_facilities.map((facility, index) => (
                                            <tr key={index}>
                                                <td>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={facility.name || ''}
                                                        onChange={(e) => handleFacilityChange(index, 'name', e.target.value)}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={facility.address || ''}
                                                        onChange={(e) => handleFacilityChange(index, 'address', e.target.value)}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={facility.phone || ''}
                                                        onChange={(e) => handleFacilityChange(index, 'phone', e.target.value)}
                                                    />
                                                </td>
                                                <td>
                                                    <select
                                                        className="form-select"
                                                        value={facility.type || 'hospital'}
                                                        onChange={(e) => handleFacilityChange(index, 'type', e.target.value)}
                                                    >
                                                        <option value="hospital">Hôpital</option>
                                                        <option value="clinic">Clinique</option>
                                                        <option value="pharmacy">Pharmacie</option>
                                                    </select>
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={facility.latitude || ''}
                                                        onChange={(e) => handleFacilityChange(index, 'latitude', e.target.value)}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={facility.longitude || ''}
                                                        onChange={(e) => handleFacilityChange(index, 'longitude', e.target.value)}
                                                    />
                                                </td>
                                                <td>
                                                    <div className="btn-group">
                                                        <button
                                                            className="btn btn-sm btn-success"
                                                            onClick={() => handleUpdateFacility(index)}
                                                            title="Enregistrer">
                                                            <i className="bi bi-check-lg"></i>
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-danger"
                                                            onClick={() => handleRemoveFacility(index)}
                                                            title="Supprimer">
                                                            <i className="bi bi-trash"></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="card bg-light">
                                <div className="card-header">
                                    <h6 className="mb-0">Ajouter un nouvel établissement médical</h6>
                                </div>
                                <div className="card-body">
                                    <div className="row g-3 mb-3">
                                        <div className="col-md-4">
                                            <label className="form-label">Nom</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={newFacility.name}
                                                onChange={(e) => handleNewFacilityChange('name', e.target.value)}
                                            />
                                        </div>
                                        <div className="col-md-4">
                                            <label className="form-label">Adresse</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={newFacility.address}
                                                onChange={(e) => handleNewFacilityChange('address', e.target.value)}
                                            />
                                        </div>
                                        <div className="col-md-4">
                                            <label className="form-label">Téléphone</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={newFacility.phone}
                                                onChange={(e) => handleNewFacilityChange('phone', e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="row g-3 mb-3">
                                        <div className="col-md-4">
                                            <label className="form-label">Type</label>
                                            <select
                                                className="form-select"
                                                value={newFacility.type}
                                                onChange={(e) => handleNewFacilityChange('type', e.target.value)}
                                            >
                                                <option value="hospital">Hôpital</option>
                                                <option value="clinic">Clinique</option>
                                                <option value="pharmacy">Pharmacie</option>
                                            </select>
                                        </div>
                                        <div className="col-md-4">
                                            <label className="form-label">Latitude</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={newFacility.latitude}
                                                onChange={(e) => handleNewFacilityChange('latitude', e.target.value)}
                                            />
                                        </div>
                                        <div className="col-md-4">
                                            <label className="form-label">Longitude</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={newFacility.longitude}
                                                onChange={(e) => handleNewFacilityChange('longitude', e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="text-end">
                                        <button
                                            className="btn btn-primary"
                                            onClick={handleAddFacility}
                                            disabled={!newFacility.name || !newFacility.address}>
                                            Ajouter l'établissement
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Informations de sécurité */}
                <div className="col-md-12 mb-4">
                    <div className="card shadow-sm">
                        <div className="card-header bg-danger text-white">
                            <h5 className="mb-0">Informations de sécurité</h5>
                        </div>
                        <div className="card-body">
                            {emergencyData.components_emergency_safety_infos.map((safetyInfo, index) => (
                                <div key={index} className="card mb-3">
                                    <div className="card-header d-flex justify-content-between align-items-center">
                                        <div className="d-flex align-items-center">
                                            <input
                                                type="text"
                                                className="form-control me-2"
                                                value={safetyInfo.title || ''}
                                                onChange={(e) => handleSafetyInfoChange(index, 'title', e.target.value)}
                                                placeholder="Titre"
                                            />
                                            <div className="input-group" style={{ width: '250px' }}>
                                                <span className="input-group-text">Priorité</span>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    min="0"
                                                    value={safetyInfo.priority || 0}
                                                    onChange={(e) => handleSafetyInfoChange(index, 'priority', parseInt(e.target.value))}
                                                />
                                            </div>
                                        </div>
                                        <div className="btn-group">
                                            <button
                                                className="btn btn-sm btn-success"
                                                onClick={() => handleUpdateSafetyInfo(index)}
                                                title="Enregistrer">
                                                <i className="bi bi-check-lg"></i>
                                            </button>
                                            <button
                                                className="btn btn-sm btn-danger"
                                                onClick={() => handleRemoveSafetyInfo(index)}
                                                title="Supprimer">
                                                <i className="bi bi-trash"></i>
                                            </button>
                                        </div>
                                    </div>
                                    <div className="card-body">
                                        <textarea
                                            className="form-control"
                                            id="content"
                                            name="content"
                                            rows="3"
                                            defaultValue={safetyInfo.content || ''}
                                            onChange={(e) => handleSafetyInfoChange(index, 'content', e.target.value)}
                                        ></textarea>
                                    </div>
                                </div>
                            ))}

                            <div className="card bg-light">
                                <div className="card-header">
                                    <h6 className="mb-0">Ajouter une nouvelle information de sécurité</h6>
                                </div>
                                <div className="card-body">
                                    <div className="row g-3 mb-3">
                                        <div className="col-md-8">
                                            <label className="form-label">Titre</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={newSafetyInfo.title}
                                                onChange={(e) => handleNewSafetyInfoChange('title', e.target.value)}
                                            />
                                        </div>
                                        <div className="col-md-4">
                                            <label className="form-label">Priorité</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                min="0"
                                                value={newSafetyInfo.priority}
                                                onChange={(e) => handleNewSafetyInfoChange('priority', parseInt(e.target.value))}
                                            />
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Contenu</label>
                                        <textarea
                                            className="form-control"
                                            rows="4"
                                            value={newSafetyInfo.content}
                                            onChange={(e) => handleNewSafetyInfoChange('content', e.target.value)}
                                        ></textarea>
                                    </div>
                                    <div className="text-end">
                                        <button
                                            className="btn btn-primary"
                                            onClick={handleAddSafetyInfo}
                                            disabled={!newSafetyInfo.title}>
                                            Ajouter l'information de sécurité
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EmergencyInformation;